import React from 'react';
import { useTranslation } from 'react-i18next';
import { Activity, BarChart3, Workflow, Loader2, Zap, Lock } from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { formatDateFr } from './payrollExportsHelpers';

const TABS = [
  { id: 'overview',   labelKey: 'payrollUi.header.overview',  icon: Activity },
  { id: 'analytics',  labelKey: 'payrollUi.header.analytics', icon: BarChart3 },
  { id: 'workflow',   labelKey: 'payrollUi.header.workflow',  icon: Workflow },
];

const PayrollHeader = ({
  realTimeData,
  periods,
  selectedPeriodId,
  setSelectedPeriodId,
  calculating,
  handleCalculatePeriod,
  periodClosed = false,
  dashboardView,
  setDashboardView,
}) => {
  const { t } = useTranslation();
  return (
  <div className="flex flex-col gap-6">
    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
      <div>
        <h3 className="text-2xl font-bold" style={{ color: "var(--lm-ink)", letterSpacing: "-0.6px" }}>
          {t("payrollUi.header.title")}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t("payrollUi.header.subtitle")}
        </p>
        <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
          <Activity className="h-3 w-3" />
          <span>{t("payrollUi.header.lastUpdate")} {realTimeData.lastUpdate.toLocaleTimeString()}</span>
          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Select value={selectedPeriodId} onValueChange={setSelectedPeriodId}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder={t("payrollUi.header.selectPeriod")} />
          </SelectTrigger>
          <SelectContent>
            {periods.map((period) => (
              <SelectItem key={period.id} value={period.id.toString()}>
                {period.description || t("payrollUi.header.periodFrom", { date: formatDateFr(period.start_date) })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          onClick={handleCalculatePeriod}
          disabled={!selectedPeriodId || calculating || periodClosed}
          title={periodClosed ? t("payrollUi.header.periodClosed") : undefined}
          className="flex items-center gap-2"
          style={{ backgroundColor: "#5e6ad2", color: "#ffffff" }}
        >
          {calculating ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("payrollUi.header.calculating")}
            </>
          ) : periodClosed ? (
            <>
              <Lock className="h-4 w-4" />
              {t("payrollUi.header.periodClosed")}
            </>
          ) : (
            <>
              <Zap className="h-4 w-4" />
              {t("payrollUi.header.calculate")}
            </>
          )}
        </Button>
      </div>
    </div>

    <div className="border-b border-border">
      <nav className="flex space-x-8">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setDashboardView(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm transition-all duration-200 ${
              dashboardView === tab.id
                ? 'border-primary text-primary bg-primary/5'
                : 'border-transparent text-muted-foreground hover:text-muted-foreground hover:border-border'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {t(tab.labelKey)}
          </button>
        ))}
      </nav>
    </div>
  </div>
  );
};

export default PayrollHeader;
